export type CueAction = "play" | "pause" | "stop";
export type CueTrigger = "time" | "button" | "global_time";

export type Cue = {
    index: number;
    line: number;
    fields: Record<string, string>;
    label?: string;
    action?: CueAction;
    trigger?: CueTrigger;
    file?: string;
    triggerValue?: string;
    atMs?: number;
    afterMs?: number;
    gain?: number;
    fadeIn?: number;
    fadeOut?: number;
};

export type EditableCue = {
    id: string;
    label: string;
    action: CueAction;
    trigger: CueTrigger;
    file: string;
    triggerValue: string;
    atMs: string;
    afterMs: string;
    gain: string;
    fadeIn: string;
    fadeOut: string;
};

export type ParseMessage = {
    level: "error" | "warning";
    message: string;
    line?: number;
    cueIndex?: number;
};

export type ParseResult = {
    cues: Cue[];
    errors: ParseMessage[];
    warnings: ParseMessage[];
};

export type TimelineEvent = {
    timeMs: number;
    action: CueAction;
    file: string;
    cueIndex: number;
    line: number;
    trigger: CueTrigger;
};

export type TimelineTrack = {
    label: string;
    maxTimeMs: number;
    events: TimelineEvent[];
};

export const MAX_CUES = 100;
const ACTIONS: Set<string> = new Set(["play", "pause", "stop"]);
const TRIGGERS: Set<string> = new Set(["time", "button", "global_time"]);

const keyMap: Record<string, string> = {
    "label": "label",
    "action": "action",
    "type": "action",
    "file": "file",
    "trigger": "trigger",
    "trigger_value": "trigger_value",
    "trigger-value": "trigger_value",
    "triggerValue": "trigger_value",
    "triggervalue": "trigger_value",
    "at_ms": "at_ms",
    "atms": "at_ms",
    "after_ms": "after_ms",
    "afterms": "after_ms",
    "gain": "gain",
    "fadein": "fade_in",
    "fade_in": "fade_in",
    "fadeout": "fade_out",
    "fade_out": "fade_out"
};

const stripInlineComment = (value: string) => {
    let inSingle = false;
    let inDouble = false;
    for (let i = 0; i < value.length; i++) {
        const char = value[i];
        if (char === "'" && !inDouble) {
            inSingle = !inSingle;
        } else if (char === '"' && !inSingle) {
            inDouble = !inDouble;
        } else if (char === "#" && !inSingle && !inDouble) {
            return value.slice(0, i).trimEnd();
        }
    }
    return value;
};

const stripQuotes = (value: string) => {
    if ((value.startsWith("\"") && value.endsWith("\"")) || (value.startsWith("'") && value.endsWith("'"))) {
        return value.slice(1, -1);
    }
    return value;
};

const parseNumber = (value: string, cueIndex: number, field: string, errors: ParseMessage[]) => {
    if (value.trim() === "") {
        return undefined;
    }
    const parsed = field === "gain" ? Number.parseFloat(value) : Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) {
        errors.push({
            level: "error",
            message: `Invalid ${field} value "${value}".`,
            cueIndex
        });
        return undefined;
    }
    return parsed;
};

const createCueId = () => `cue_${Math.random().toString(36).slice(2, 10)}`;

const toStringOrEmpty = (value?: number) => {
    return value === undefined ? "" : value.toString();
};

export const createEditableCue = (): EditableCue => ({
    id: createCueId(),
    label: "",
    action: "play",
    trigger: "time",
    file: "",
    triggerValue: "",
    atMs: "0",
    afterMs: "0",
    gain: "",
    fadeIn: "",
    fadeOut: ""
});

export const cloneEditableCue = (cue: EditableCue): EditableCue => ({
    ...cue,
    id: createCueId()
});

export const editableCueFromParsed = (cue: Cue): EditableCue => ({
    id: createCueId(),
    label: cue.label || "",
    action: cue.action || "play",
    trigger: cue.trigger || "time",
    file: cue.file || "",
    triggerValue: cue.triggerValue || "",
    atMs: cue.atMs === undefined ? "0" : toStringOrEmpty(cue.atMs),
    afterMs: cue.afterMs === undefined ? "0" : toStringOrEmpty(cue.afterMs),
    gain: toStringOrEmpty(cue.gain),
    fadeIn: toStringOrEmpty(cue.fadeIn),
    fadeOut: toStringOrEmpty(cue.fadeOut)
});

const integerPattern = /^-?\d+$/;
const floatPattern = /^-?(\d+(\.\d+)?|\.\d+)$/;

const formatIntegerField = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return undefined;
    }
    if (!integerPattern.test(trimmed)) {
        return undefined;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return undefined;
    }
    return trimmed;
};

const formatFloatField = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return undefined;
    }
    if (!floatPattern.test(trimmed)) {
        return undefined;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed)) {
        return undefined;
    }
    return trimmed;
};

const formatStringField = (value: string) => {
    return value.trim();
};

export const serializeEditableCues = (cues: EditableCue[]) => {
    const lines: string[] = [];
    cues.forEach((cue, index) => {
        if (index > 0) {
            lines.push("");
        }
        lines.push("[cue]");
        const label = formatStringField(cue.label);
        if (label !== "") {
            lines.push(`label = "${label}"`);
        }
        lines.push(`action = "${cue.action}"`);
        const fileValue = formatStringField(cue.file);
        if (fileValue !== "" || cue.action === "stop") {
            lines.push(`file = "${fileValue}"`);
        }
        lines.push(`trigger = "${cue.trigger}"`);
        if (cue.trigger === "button") {
            const triggerValue = formatStringField(cue.triggerValue);
            if (triggerValue !== "") {
                lines.push(`trigger_value = "${triggerValue}"`);
            }
        }

        if (cue.trigger === "global_time") {
            const atMs = formatIntegerField(cue.atMs);
            if (atMs !== undefined) {
                lines.push(`at_ms = ${atMs}`);
            }
        }

        if (cue.trigger === "time") {
            const afterMs = formatIntegerField(cue.afterMs);
            if (afterMs !== undefined) {
                lines.push(`after_ms = ${afterMs}`);
            }
        }

        const gain = formatFloatField(cue.gain);
        if (gain !== undefined) {
            lines.push(`gain = ${gain}`);
        }
        const fadeIn = formatIntegerField(cue.fadeIn);
        if (fadeIn !== undefined) {
            lines.push(`fadeIn = ${fadeIn}`);
        }
        const fadeOut = formatIntegerField(cue.fadeOut);
        if (fadeOut !== undefined) {
            lines.push(`fadeOut = ${fadeOut}`);
        }
    });
    return lines.join("\n");
};

export const parseCuesText = (text: string): ParseResult => {
    const lines = text.split(/\r?\n/);
    const cues: Cue[] = [];
    const errors: ParseMessage[] = [];
    const warnings: ParseMessage[] = [];
    let currentCue: Cue | null = null;

    const pushCue = () => {
        if (currentCue) {
            cues.push(currentCue);
            currentCue = null;
        }
    };

    lines.forEach((rawLine, index) => {
        const lineNumber = index + 1;
        const trimmed = rawLine.trim();
        if (trimmed === "" || trimmed.startsWith("#")) {
            return;
        }
        if (trimmed.toLowerCase() === "[cue]") {
            pushCue();
            currentCue = {
                index: cues.length,
                line: lineNumber,
                fields: {}
            };
            return;
        }

        if (!currentCue) {
            return;
        }

        const withoutComment = stripInlineComment(trimmed);
        const eqIndex = withoutComment.indexOf("=");
        if (eqIndex === -1) {
            warnings.push({
                level: "warning",
                message: "Ignored because it does not contain '='.",
                cueIndex: currentCue.index
            });
            return;
        }

        const rawKey = withoutComment.slice(0, eqIndex).trim();
        const rawValue = withoutComment.slice(eqIndex + 1).trim();
        const normalizedKey = keyMap[rawKey.toLowerCase().replace(/\s+/g, "")];
        if (!normalizedKey) {
            warnings.push({
                level: "warning",
                message: `Unknown key "${rawKey}".`,
                line: lineNumber,
                cueIndex: currentCue.index
            });
            return;
        }

        currentCue.fields[normalizedKey] = stripQuotes(rawValue);
    });

    pushCue();

    if (cues.length > MAX_CUES) {
        errors.push({
            level: "error",
            message: `Cue count ${cues.length} exceeds the limit of ${MAX_CUES}.`
        });
    }

    let lastButtonCueIndex = -1;
    cues.forEach((cue, idx) => {
        cue.index = idx;
        const action = cue.fields["action"]?.toLowerCase();
        if (!action) {
            errors.push({
                level: "error",
                message: "Missing action.",
                cueIndex: idx
            });
        } else if (!ACTIONS.has(action)) {
            errors.push({
                level: "error",
                message: `Unknown action "${action}".`,
                cueIndex: idx
            });
        } else {
            cue.action = action as CueAction;
        }

        const trigger = (cue.fields["trigger"] || "time").toLowerCase();
        if (!TRIGGERS.has(trigger)) {
            errors.push({
                level: "error",
                message: `Unknown trigger "${trigger}".`,
                cueIndex: idx
            });
        } else {
            cue.trigger = trigger as CueTrigger;
            if (cue.trigger === "button") {
                lastButtonCueIndex = idx;
            }
        }

        const file = cue.fields["file"];
        if (cue.action === "play" || cue.action === "pause") {
            if (file === undefined || file === "") {
                errors.push({
                    level: "error",
                    message: "Missing file for play/pause action.",
                    cueIndex: idx
                });
            }
        }
        if (file !== undefined) {
            cue.file = file;
            if (file !== "" && !file.toLowerCase().endsWith(".wav")) {
                errors.push({
                    level: "error",
                    message: "File must end with .wav.",
                    cueIndex: idx
                });
            }
        }

        const label = cue.fields["label"];
        if (label !== undefined) {
            cue.label = label;
        }

        const triggerValue = cue.fields["trigger_value"];
        if (triggerValue !== undefined) {
            cue.triggerValue = triggerValue;
            if (cue.trigger !== "button") {
                warnings.push({
                    level: "warning",
                    message: "trigger_value is set but trigger is not button.",
                    cueIndex: idx
                });
            }
        }

        cue.atMs = parseNumber(cue.fields["at_ms"] ?? "", idx, "at_ms", errors);
        cue.afterMs = parseNumber(cue.fields["after_ms"] ?? "", idx, "after_ms", errors);
        cue.gain = parseNumber(cue.fields["gain"] ?? "", idx, "gain", errors);
        cue.fadeIn = parseNumber(cue.fields["fade_in"] ?? "", idx, "fadeIn", errors);
        cue.fadeOut = parseNumber(cue.fields["fade_out"] ?? "", idx, "fadeOut", errors);

        if (cue.afterMs !== undefined && cue.trigger !== "time") {
            warnings.push({
                level: "warning",
                message: "after_ms is set but trigger is not time.",
                cueIndex: idx
            });
        }

        if (cue.atMs !== undefined && cue.trigger !== "global_time") {
            warnings.push({
                level: "warning",
                message: "at_ms is set but trigger is not global_time.",
                cueIndex: idx
            });
        }

        if (cue.afterMs !== undefined && lastButtonCueIndex < 0) {
            errors.push({
                level: "error",
                message: "after_ms is set but there is no preceding button cue.",
                cueIndex: idx
            });
        }

        if (cue.gain !== undefined && cue.gain <= 0) {
            warnings.push({
                level: "warning",
                message: "gain <= 0 is treated as 1.0 on the device.",
                cueIndex: idx
            });
        }
    });

    return {cues, errors, warnings};
};

type ScheduledCue = {
    cue: Cue;
    timeMs: number;
};

type ButtonBlock = {
    index: number;
    cues: ScheduledCue[];
};

const buildAbsoluteSchedule = (cues: Cue[]): ScheduledCue[] => {
    return cues
        .filter((cue) => cue.trigger === "global_time" && cue.atMs !== undefined)
        .map((cue) => ({
            cue,
            timeMs: cue.atMs as number
        }));
};

const buildButtonBlocks = (cues: Cue[]): ButtonBlock[] => {
    const blocks: ButtonBlock[] = [];
    for (let i = 0; i < cues.length; i++) {
        const cue = cues[i];
        if (cue.trigger !== "button") {
            continue;
        }
        const scheduled: ScheduledCue[] = [{
            cue,
            timeMs: 0
        }];
        for (let j = i + 1; j < cues.length; j++) {
            const next = cues[j];
            if (next.trigger === "button") {
                break;
            }
            if (next.trigger === "time" && next.afterMs !== undefined) {
                scheduled.push({
                    cue: next,
                    timeMs: next.afterMs as number
                });
            }
        }
        blocks.push({index: i, cues: scheduled});
    }
    return blocks;
};

const simulateSchedule = (label: string, schedule: ScheduledCue[]): ParseMessage[] => {
    const warnings: ParseMessage[] = [];
    const activeFiles = new Set<string>();
    const ordered = schedule.slice().sort((a, b) => {
        if (a.timeMs !== b.timeMs) {
            return a.timeMs - b.timeMs;
        }
        return a.cue.index - b.cue.index;
    });

    ordered.forEach(({cue}) => {
        if (!cue.action) {
            return;
        }
        const file = cue.file ?? "";
        const hasFile = file !== "";
        if (cue.action === "play") {
            if (!hasFile) {
                return;
            }
            if (!activeFiles.has(file) && activeFiles.size >= 2) {
                warnings.push({
                    level: "warning",
                    message: `Playback overlap in ${label}: starting "${file}" while ${activeFiles.size} files are active.`,
                    cueIndex: cue.index
                });
                return;
            }
            activeFiles.add(file);
            return;
        }
        if (cue.action === "pause") {
            if (hasFile) {
                activeFiles.delete(file);
            }
            return;
        }
        if (cue.action === "stop") {
            if (!hasFile) {
                activeFiles.clear();
                return;
            }
            activeFiles.delete(file);
        }
    });

    return warnings;
};

export const getPlaybackWarnings = (cues: Cue[]): ParseMessage[] => {
    const warnings: ParseMessage[] = [];
    const absoluteSchedule = buildAbsoluteSchedule(cues);
    if (absoluteSchedule.length > 0) {
        warnings.push(...simulateSchedule("absolute timeline", absoluteSchedule));
    }
    const buttonBlocks = buildButtonBlocks(cues);
    buttonBlocks.forEach((block, index) => {
        const label = `button block ${index + 1}`;
        warnings.push(...simulateSchedule(label, block.cues));
    });
    return warnings;
};

const toTimelineEvents = (schedule: ScheduledCue[]): TimelineEvent[] => {
    return schedule
        .filter(({cue}) => !!cue.action)
        .map(({cue, timeMs}) => ({
            timeMs,
            action: cue.action as CueAction,
            file: cue.file ?? "",
            cueIndex: cue.index,
            line: cue.line,
            trigger: cue.trigger || "time"
        }));
};

const getMaxTime = (events: TimelineEvent[]) => {
    return events.reduce((max, event) => Math.max(max, event.timeMs), 0);
};

export const getTimelineTracks = (cues: Cue[]): TimelineTrack[] => {
    const tracks: TimelineTrack[] = [];
    const absoluteSchedule = buildAbsoluteSchedule(cues);
    const absoluteEvents = toTimelineEvents(absoluteSchedule);
    if (absoluteEvents.length > 0) {
        tracks.push({
            label: "Absolute timeline",
            maxTimeMs: getMaxTime(absoluteEvents),
            events: absoluteEvents
        });
    }

    const buttonBlocks = buildButtonBlocks(cues);
    buttonBlocks.forEach((block, index) => {
        const events = toTimelineEvents(block.cues);
        if (events.length === 0) {
            return;
        }
        const buttonCue = block.cues[0]?.cue;
        const triggerLabel = buttonCue?.triggerValue ? ` - ${buttonCue.triggerValue}` : "";
        tracks.push({
            label: `Button block ${index + 1} (cue ${buttonCue?.index !== undefined ? buttonCue.index + 1 : "?"}${triggerLabel})`,
            maxTimeMs: getMaxTime(events),
            events: events
        });
    });

    return tracks;
};
